import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { Dialog, DialogContent, Box, Card, CardContent, Typography, Divider, DialogActions } from '@mui/material'
import { useNotification } from '@/hooks/useNotification'
import { useCreateEmployeeMutation } from '@/@data'
import { useTranslator } from '@/hooks/useTranslator'
import { Button } from '@/@core/components/mui/Button'

interface EmployeeConfirmRestoreDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  employee: any
  refetch?: () => void
}

export const EmployeeConfirmRestoreDialog = ({
  open,
  onClose,
  employee,
  onSuccess,
  refetch
}: EmployeeConfirmRestoreDialogProps) => {
  const { __ } = useTranslator()
  const n = useNotification()
  // const submitDeleteUserMutation = useSubmitDeleteUserMutation()

  const createEmployee = useCreateEmployeeMutation()

  if (!employee) return null

  const handleRestore = () => {
    createEmployee.mutate(
      {
        company: employee.company,
        company_id: employee.company_id,
        first_name: employee.first_name,
        employee_id: employee.employee_id,
        joined_date: employee.joined_date,
        contract_type: employee.contract_type,
        phone_code: employee.phone_code,
        phone: employee.phone,
        bank_id: employee.bank_id,
        bank_account_number: employee.bank_account_number,
        bank_account_name: employee.bank_account_name,
        identity_card: employee.identity_card,
        identity_card_issue_date: employee.identity_card_issue_date,
        identity_card_issue_place: employee.identity_card_issue_place,
        gender: employee.gender,
        address: employee.address,
        birth_date: employee.birth_date,
        dependent: employee.dependent,
        tenure: employee.tenure,
        confirm_create: true
      },
      {
        onSuccess: () => {
          n.success(__('Confirm Add Employee Success'))
          onSuccess?.()
          refetch?.()
          onClose()
        },
        onError: error => {
          n.error(error?.message ? __(error.message) : __('Confirm Add Employee failed'))
        }
      }
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      scroll='body'
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '650px', borderRadius: '32px' }
      }}
    >
      <DialogContent className='p-0 m-0' sx={{ padding: 0, borderRadius: '32px' }}>
        <DialogCloseButton onClick={onClose} disableRipple>
          <i className='tabler-x' />
        </DialogCloseButton>
        <Card>
          <CardContent>
            <Typography variant='h5' className='text-[#2F2B3DE5] mb-6'>
              {__('Confirm Request Delete Employee')}
            </Typography>

            <Box className='flex items-center justify-center gap-6'>
              <Box className='flex-1 rounded-2xl bg-[#3551F214] p-6 border border-[#3551F226]'>
                <Typography variant='h6' className='text-[#2F2B3DE5]'>
                  {__('Employee Information')}
                </Typography>
                <Divider className='my-4' />
                <Box className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employee Name')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.first_name || employee?.last_name
                        ? `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()
                        : '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employer ID')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee.company_id || '-'}
                      {/* {companyName} */}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employee Code')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.employee_id || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Phone')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.phone_code} {employee?.phone || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Typography className='text-[15px] leading-[22px] text-[#FF4C51] mt-3'>
              {__('Warning: This user has requested account deletion. Are you sure you want to create a new user?')}
            </Typography>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions className='px-[20px] sm:px-[24px] text-center justify-end gap-2'>
        <Button variant='outlined' onClick={onClose} startIcon={<i className='tabler-arrow-left' />}>
          {__('Cancel')}
        </Button>
        <Button
          variant='contained'
          startIcon={<i className='tabler-checks' />}
          color='success'
          disabled={createEmployee.isPending}
          onClick={handleRestore}
        >
          {createEmployee.isPending ? __('Confirm Create User...') : __('Confirm Create User')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
